import {
  type RefObject,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Link, Outlet, useLocation } from 'react-router';
import { useAuth } from '../auth/auth-context.js';
import { istSichtbar } from '../runs/einsicht.js';
import { useWartendeKontext } from '../runs/wartende-kontext.js';
import { klasse } from './klasse.js';
import {
  aktiverPunkt,
  anzahlFuer,
  NAVIGATION,
  type NavGruppe,
  type NavPunkt,
  type WartendeZahlen,
  zaehleWartende,
  zielVon,
} from './navigation.js';
import { SeitenleistenKontext, type SeitenleistenWert } from './seitenleiste-kontext.js';
import stile from './shell.module.css';
import { type NutzungsDienst, WillkommenDialog } from './willkommen-dialog.js';
import { LinienSymbol, type LinienSymbolName } from './linien-symbol.js';

interface Zaehlung {
  zahlen: WartendeZahlen;
}

// Exakte Zahl, ohne "+" am Deckel (Vorgabe 2026-10-16: nie "mindestens").
function Zaehler({ anzahl }: { anzahl: number }) {
  if (anzahl === 0) return null;
  return (
    <>
      {/* Die Zahl ist fuer das Auge, der Satz fuer Hilfstechnik. Getrennt, weil Inline-Elemente
          im berechneten Namen ohne Leerzeichen aneinanderstossen ("Review2"). */}
      <span className={stile.zaehler} aria-hidden="true">
        {anzahl}
      </span>
      <span className={stile.nurVorlesen}>, {anzahl} warten auf Freigabe</span>
    </>
  );
}

const SPEICHER_SCHLUESSEL = 'awf-seitenleiste-eingeklappt';

// Speicher kann fehlen (privates Fenster, gesperrte Website-Daten) und dann werfen. Die Wahl
// gilt dann nur fuer diese Sitzung -- ein Absturz des Rahmens waere der schlechtere Tausch.
function leseEingeklappt(): boolean {
  try {
    return localStorage.getItem(SPEICHER_SCHLUESSEL) === '1';
  } catch {
    return false;
  }
}

function speichereEingeklappt(wert: boolean): void {
  try {
    localStorage.setItem(SPEICHER_SCHLUESSEL, wert ? '1' : '0');
  } catch {
    // siehe leseEingeklappt
  }
}

/**
 * Drei Quellen, eine klare Rangfolge: Fordert eine Seite das Einklappen an, gilt das -- es
 * sei denn, die Nutzerin klappt waehrend dieses Besuchs bewusst auf. Ohne Anforderung gilt
 * die gespeicherte Wahl. Die Anforderung wird GEZAEHLT statt als Schalter gefuehrt, damit
 * StrictMode (Effekt laeuft zweimal) und ueberlappende Seitenwechsel sie nicht verlieren.
 */
function useEinklappen() {
  const [gespeichert, setGespeichert] = useState(leseEingeklappt);
  const [anforderungen, setAnforderungen] = useState(0);
  const [aufTrotzAnforderung, setAufTrotzAnforderung] = useState(false);

  useEffect(() => {
    if (anforderungen === 0) setAufTrotzAnforderung(false);
  }, [anforderungen]);

  const fordereEinklappenAn = useCallback(() => {
    setAnforderungen((n) => n + 1);
    return () => setAnforderungen((n) => n - 1);
  }, []);

  const umschalten = useCallback(() => {
    if (anforderungen > 0) {
      setAufTrotzAnforderung((a) => !a);
      return;
    }
    const neu = !gespeichert;
    setGespeichert(neu);
    speichereEingeklappt(neu);
  }, [anforderungen, gespeichert]);

  const kontext = useMemo<SeitenleistenWert>(
    () => ({ fordereEinklappenAn }),
    [fordereEinklappenAn],
  );
  const eingeklappt = anforderungen > 0 ? !aufTrotzAnforderung : gespeichert;
  return { eingeklappt, umschalten, kontext };
}

function PunktLink({
  punkt,
  symbol,
  aktiv,
  zaehlung,
  schmal,
}: {
  punkt: NavPunkt;
  symbol?: LinienSymbolName;
  aktiv: string | null;
  zaehlung: Zaehlung | null;
  /** Nur in der schmalen Leiste ist der Text selbst verdeckt -- dort braucht es den Tooltip. */
  schmal?: boolean;
}) {
  return (
    <Link
      to={zielVon(punkt)}
      className={klasse(stile.eintrag, !symbol && stile.unterpunkt)}
      // Von Hand statt NavLink: NavLink vergleicht nur den Pfad, nicht die Suchparameter.
      // Das CSS haengt am Attribut, nicht an einer Klasse.
      aria-current={aktiv === punkt.schluessel ? 'page' : undefined}
      title={schmal ? punkt.text : undefined}
    >
      {symbol && <LinienSymbol name={symbol} />}
      <span className={stile.eintragText}>{punkt.text}</span>
      <Zaehler anzahl={zaehlung ? anzahlFuer(punkt.zaehler, zaehlung.zahlen) : 0} />
    </Link>
  );
}

function Gruppe({
  gruppe,
  aktiv,
  eingeklappt,
  zaehlung,
}: {
  gruppe: NavGruppe;
  aktiv: string | null;
  eingeklappt: boolean;
  zaehlung: Zaehlung | null;
}) {
  const enthaeltAktiven = gruppe.punkte.some((p) => p.schluessel === aktiv);
  const [offen, setOffen] = useState(enthaeltAktiven);
  // Wer ueber einen Link in eine zugeklappte Gruppe springt, soll sehen, wo er gelandet ist.
  useEffect(() => {
    if (enthaeltAktiven) setOffen(true);
  }, [enthaeltAktiven]);
  const listenId = `navigation-${gruppe.schluessel}`;

  const [erster] = gruppe.punkte;
  if (eingeklappt && erster) {
    // In der schmalen Leiste ist kein Platz fuer Unterpunkte; die Gruppe fuehrt wie im
    // Mock-up zu ihrem ersten Punkt. Der Name bleibt fuer Hilfstechnik lesbar.
    return (
      <Link
        to={zielVon(erster)}
        className={klasse(stile.eintrag)}
        data-aktiv={enthaeltAktiven ? 'true' : undefined}
        // Nicht 'page': Der Link fuehrt zum ersten Punkt der Gruppe, nicht notwendig zur
        // exakten aktiven Adresse. 'true' markiert trotzdem, dass diese Gruppe es ist.
        aria-current={enthaeltAktiven ? 'true' : undefined}
        title={gruppe.text}
      >
        <LinienSymbol name={gruppe.symbol} />
        <span className={stile.eintragText}>{gruppe.text}</span>
      </Link>
    );
  }

  return (
    <div>
      <button
        type="button"
        className={stile.gruppenkopf}
        aria-expanded={offen}
        aria-controls={listenId}
        onClick={() => setOffen((o) => !o)}
      >
        <LinienSymbol name={gruppe.symbol} />
        <span className={stile.eintragText}>{gruppe.text}</span>
        <span className={stile.pfeil} data-offen={offen ? 'true' : undefined}>
          <LinienSymbol name="chevron-right" groesse={14} />
        </span>
      </button>
      {/* `hidden` statt Animation: Zugeklappte Punkte verschwinden damit auch fuer
          Hilfstechnik und Tab-Reihenfolge eindeutig. */}
      <div id={listenId} className={stile.unterpunkte} hidden={!offen}>
        {gruppe.punkte.map((p) => (
          <PunktLink key={p.schluessel} punkt={p} aktiv={aktiv} zaehlung={zaehlung} />
        ))}
      </div>
    </div>
  );
}

/** Seitenleisten-Kopf bleibt nur auf schmalen Fenstern: dort traegt er den Menueknopf. */
function Kopf({
  mobilOffen,
  schalteMobil,
  menueKnopfRef,
}: {
  mobilOffen: boolean;
  schalteMobil: () => void;
  /** Von der Shell gehalten: Escape beim Verlassen der Ueberlagerung muss dorthin zurueck. */
  menueKnopfRef: RefObject<HTMLButtonElement | null>;
}) {
  return (
    <header className={stile.kopf}>
      <button
        ref={menueKnopfRef}
        type="button"
        className={stile.menueKnopf}
        aria-controls="seitenleiste"
        aria-expanded={mobilOffen}
        aria-label={mobilOffen ? 'Navigation schließen' : 'Navigation öffnen'}
        onClick={schalteMobil}
      >
        <LinienSymbol name="menu" groesse={24} />
      </button>
    </header>
  );
}

/**
 * Zwei Buchstaben fuer den Avatar: aus dem Namen (Vor- und Nachname), sonst aus dem lokalen
 * Teil der Adresse (`erika.muster@` -> "EM"). Wie in der Content-Factory (`initialen`).
 */
export function initialen(name: string | null | undefined, email: string | null | undefined) {
  const quelle = name?.trim() || (email ?? '').split('@')[0] || '';
  const teile = quelle.split(/[\s._-]+/u).filter(Boolean);
  if (teile.length === 0) return '?';
  return teile
    .slice(0, 2)
    .map((teil) => [...teil][0]!.toUpperCase())
    .join('');
}

/**
 * Nutzerbereich unten links wie in der Content-Factory (`.userchip`): Avatar mit Initialen,
 * Name und Adresse, darunter Abmelden. Vorher stand das Konto oben rechts in einem Ausklappmenue.
 * In der schmalen Leiste bleibt nur der Avatar; Abmelden ist dann einen Klick entfernt
 * (ausklappen), wie dort ("logout unten soll weg", Content-Factory 23.08.2026).
 */
function Nutzerbereich({ schmal }: { schmal: boolean }) {
  const { nutzer, meldeAb } = useAuth();
  const name = nutzer?.name?.trim() || null;
  const email = nutzer?.email ?? '';
  return (
    <div className={stile.nutzer}>
      <div className={stile.nutzerChip} title={schmal ? (name ?? email) : undefined}>
        <span className={stile.avatar} aria-hidden="true">
          {initialen(name, email)}
        </span>
        <span className={stile.wer}>
          <span className={stile.nurVorlesen}>Angemeldet als </span>
          {name && <b className={stile.nutzerName}>{name}</b>}
          <span className={stile.nutzerMail}>{email}</span>
        </span>
      </div>
      {!schmal && (
        <button
          className={klasse('eds-button eds-button--tertiary eds-button--sm', stile.abmelden)}
          type="button"
          onClick={() => void meldeAb()}
        >
          Abmelden
        </button>
      )}
    </div>
  );
}

const ELABORATUM_URL = 'https://www.elaboratum.com/leistungen/generative-conversational-ai/';
const IMPRESSUM_URL = 'https://www.elaboratum.com/impressum/';

/**
 * Fusszeile wie in der Content-Factory: ueber die volle Breite (auch unter der Seitenleiste) und
 * immer am unteren Fensterrand sichtbar. Einordnung links, Herkunft und Impressum mittig.
 */
function Fusszeile() {
  return (
    <footer className={stile.fusszeile}>
      <span className={stile.versionsHinweis}>Alpha-Version</span>
      <p className={stile.fussLinks}>
        <a href={ELABORATUM_URL} target="_blank" rel="noopener noreferrer">
          Powered by <span className={stile.elaboratum}>elaboratum</span>
        </a>
        {' · '}
        <a href={IMPRESSUM_URL} target="_blank" rel="noopener noreferrer">
          Impressum
        </a>
      </p>
    </footer>
  );
}

export function Shell({ nutzung }: { nutzung: NutzungsDienst }) {
  const ort = useLocation();
  const aktiv = aktiverPunkt(ort.pathname, ort.search);
  const { eingeklappt, umschalten, kontext } = useEinklappen();
  const [mobilOffen, setMobilOffen] = useState(false);
  const navigationRef = useRef<HTMLElement>(null);
  const menueKnopfRef = useRef<HTMLButtonElement>(null);
  const hauptteilRef = useRef<HTMLElement>(null);
  const mobilOffenRef = useRef(mobilOffen);
  // Im Layout-Effekt statt im Render (ein Render darf keine Refs schreiben, er kann verworfen
  // werden). Layout-Effekte laufen vor den Effekten unten, die den Wert lesen.
  useLayoutEffect(() => {
    mobilOffenRef.current = mobilOffen;
  }, [mobilOffen]);
  const fokusNachSprung = useRef(false);
  // Nach jedem Sprung schliesst die Ueberlagerung, sonst verdeckt sie die neue Seite. An
  // `ort.key` statt Pfad/Suche haengen: Ein Klick auf den Link der aktuellen Seite aendert
  // beide nicht, bekommt aber einen neuen Verlaufseintrag und damit einen neuen Key. War sie
  // offen, ist der geklickte Link mit ihr verschwunden und der Fokus fiele auf <body>.
  // Deshalb wandert er auf die Ueberschrift der Zielseite.
  useEffect(() => {
    if (mobilOffenRef.current) fokusNachSprung.current = true;
    setMobilOffen(false);
  }, [ort.key]);
  // Bewusst ohne Abhaengigkeitsliste: Der Ausloeser ist der Merker `fokusNachSprung`, ein Ref, und
  // dessen Aenderung steht in keiner Liste. Der Effekt prueft deshalb nach jedem Render, ob ein
  // Sprung noch auf seinen Fokus wartet. Das kostet nur einen Vergleich und tut sonst nichts.
  useEffect(() => {
    if (!fokusNachSprung.current || mobilOffen) return;
    fokusNachSprung.current = false;
    const ziel = hauptteilRef.current?.querySelector<HTMLElement>('h1') ?? hauptteilRef.current;
    if (!ziel) return;
    // tabindex=-1: per Skript fokussierbar, ohne in die Tab-Reihenfolge zu geraten.
    if (!ziel.hasAttribute('tabindex')) ziel.setAttribute('tabindex', '-1');
    ziel.focus();
  });
  // In der Ueberlagerung ist Platz fuer die volle Leiste; die schmale gilt nur daneben.
  const schmal = eingeklappt && !mobilOffen;

  // Fenster ueber die 60rem-Grenze vergroessert: Die Seitenleiste ist wieder eine Spalte,
  // die Ueberlagerung darueber waere doppelt und unerreichbar (kein Menueknopf mehr).
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const abfrage = window.matchMedia('(min-width: 60rem)');
    const beiAenderung = (e: MediaQueryListEvent) => {
      if (e.matches) setMobilOffen(false);
    };
    abfrage.addEventListener('change', beiAenderung);
    return () => abfrage.removeEventListener('change', beiAenderung);
  }, []);

  // Fokus folgt der Ueberlagerung: Beim Oeffnen auf den ersten Navigationslink, Escape
  // schliesst und gibt ihn an den Menueknopf zurueck -- sonst faellt er auf <body>.
  useEffect(() => {
    if (!mobilOffen) return;
    navigationRef.current?.querySelector<HTMLElement>('a, button')?.focus();
    const beiTaste = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setMobilOffen(false);
        menueKnopfRef.current?.focus();
      }
    };
    document.addEventListener('keydown', beiTaste);
    return () => document.removeEventListener('keydown', beiTaste);
  }, [mobilOffen]);

  // Fehler oder Zeitgrenze: kein Zaehler, still. Die Auftragsliste meldet den Ausfall selbst
  // (eigene Warnung) -- zweimal dieselbe Meldung waere Laerm.
  const wartende = useWartendeKontext();
  const zaehlung = useMemo<Zaehlung | null>(
    () =>
      wartende.art === 'daten'
        ? {
            // Ausgeblendete zaehlen nicht als wartend.
            zahlen: zaehleWartende(wartende.auftraege.filter(istSichtbar)),
          }
        : null,
    [wartende],
  );

  return (
    <SeitenleistenKontext.Provider value={kontext}>
      <div
        className={stile.rahmen}
        data-eingeklappt={schmal ? 'true' : undefined}
        data-mobil-offen={mobilOffen ? 'true' : undefined}
      >
        <aside id="seitenleiste" className={stile.seite} aria-label="Seitenleiste">
          {/* Einklappen wie in der Content-Factory: ausgeklappt Logo plus Pfeil daneben,
              eingeklappt wird die ERGO-Bildmarke selbst zum Ausklapp-Knopf (beim Zeigen
              erscheint der Pfeil). Ein Knopf statt eines Links: zwei Handlungen an einem
              Element waeren nicht eindeutig, deshalb fuehrt die Marke eingeklappt nicht zur
              Startseite. Immer genau EIN Umschalter im Baum. */}
          <div className={stile.markenKopf}>
            {schmal ? (
              <button
                type="button"
                className={stile.markenKnopf}
                aria-controls="seitenleiste"
                aria-expanded={false}
                aria-label="Seitenleiste ausklappen"
                onClick={umschalten}
              >
                <img src="/marke/ergo-mark.svg" alt="" className={stile.bildmarke} />
                <span className={stile.ausklappPfeil} aria-hidden="true">
                  <LinienSymbol name="chevron-right" groesse={16} />
                </span>
              </button>
            ) : (
              <>
                <Link to="/" className={klasse(stile.marke)} title="Zur Übersicht">
                  <img src="/marke/ergo-logo.png" alt="ERGO" className={stile.logo} />
                  {/* Zweizeilig hinter dem Strich wie in der Content-Factory. */}
                  <span className={stile.produkt}>
                    <span>Content</span> <span>Engine</span>
                  </span>
                </Link>
                <button
                  type="button"
                  className={stile.einklappen}
                  aria-controls="seitenleiste"
                  aria-expanded={true}
                  aria-label="Seitenleiste einklappen"
                  onClick={umschalten}
                >
                  <LinienSymbol name="chevron-left" groesse={16} />
                </button>
              </>
            )}
          </div>
          <nav ref={navigationRef} className={stile.navigation} aria-label="Hauptnavigation">
            {NAVIGATION.map((eintrag) =>
              eintrag.art === 'punkt' ? (
                <PunktLink
                  key={eintrag.punkt.schluessel}
                  punkt={eintrag.punkt}
                  symbol={eintrag.symbol}
                  aktiv={aktiv}
                  zaehlung={zaehlung}
                  schmal={schmal}
                />
              ) : (
                <Gruppe
                  key={eintrag.gruppe.schluessel}
                  gruppe={eintrag.gruppe}
                  aktiv={aktiv}
                  eingeklappt={schmal}
                  zaehlung={zaehlung}
                />
              ),
            )}
          </nav>
          <div className={stile.fuss}>
            <Nutzerbereich schmal={schmal} />
          </div>
        </aside>

        <div className={stile.inhalt}>
          <Kopf
            mobilOffen={mobilOffen}
            schalteMobil={() => setMobilOffen((o) => !o)}
            menueKnopfRef={menueKnopfRef}
          />
          <main ref={hauptteilRef} className={stile.hauptteil}>
            <Outlet />
          </main>
        </div>
        <Fusszeile />
        <WillkommenDialog nutzung={nutzung} />
      </div>
    </SeitenleistenKontext.Provider>
  );
}
